var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useStore } from 'jotai/react';
import { useCallback, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { stageRefAtom } from '@/store/boardAtoms';
import { NOTE_TYPE } from '@/components/Board/NotesPanel/NoteListItem';
import { firstVisibleLayer } from '@/utils/board/boardUtils';
/**
 * Константы для создания текстовых элементов из заметок
 */
const NOTE_ELEMENT_CONFIG = {
    width: 400,
    height: 600,
    fontSize: 18,
    textAlign: 'left',
    verticalAlign: 'top',
    textColor: '#1E1E1E',
    backgroundColor: '#ffffff',
};
/**
 * Вычисляет мировые координаты из клиентских координат курсора
 */
const getWorldCoordinates = (clientX, clientY, stage, container) => {
    const rect = container.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;
    const scale = stage.scaleX();
    return {
        x: (relativeX - stage.x()) / scale,
        y: (relativeY - stage.y()) / scale,
    };
};
/**
 * Кастомный хук для обработки drag-and-drop файлов и заметок на Stage
 *
 * @param params - Параметры хука
 */
export const useStageDragAndDrop = ({ pointerPositionRef, canEdit, boardState, send, onPasteImage, onPasteImages, onPasteText, onNoteDragPositionChange, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    /**
     * Вычисляет позицию placeholder для заметки на основе координат курсора
     */
    const calculateNotePosition = useCallback((clientX, clientY) => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return null;
        const container = stage.getContent().parentElement;
        if (!container)
            return null;
        const worldCoords = getWorldCoordinates(clientX, clientY, stage, container);
        if (!worldCoords)
            return null;
        // Позиция: верхний край по центру курсора
        return {
            x: worldCoords.x - NOTE_ELEMENT_CONFIG.width / 2,
            y: worldCoords.y,
            width: NOTE_ELEMENT_CONFIG.width,
            height: NOTE_ELEMENT_CONFIG.height,
        };
    }, [store]);
    /**
     * Обработка дропа заметок через react-dnd
     */
    const [, drop] = useDrop({
        accept: NOTE_TYPE,
        drop: (item, monitor) => {
            onNoteDragPositionChange === null || onNoteDragPositionChange === void 0 ? void 0 : onNoteDragPositionChange(null);
            const stage = store.get(stageRefAtom);
            if (!canEdit || !stage)
                return;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset)
                return;
            const position = calculateNotePosition(clientOffset.x, clientOffset.y);
            if (!position)
                return;
            const firstLayer = firstVisibleLayer(boardState);
            if (!firstLayer)
                return;
            // Создаем текстовый элемент с содержимым заметки
            send({
                type: 'element.add',
                payload: {
                    layerId: firstLayer.id,
                    elementType: 'text',
                    name: item.note.name,
                    note: item.note.id,
                    props: {
                        x: position.x,
                        y: position.y,
                        w: position.width,
                        h: position.height,
                        text: item.note.text || '',
                        fontSize: NOTE_ELEMENT_CONFIG.fontSize,
                        textAlign: NOTE_ELEMENT_CONFIG.textAlign,
                        verticalAlign: NOTE_ELEMENT_CONFIG.verticalAlign,
                        textColor: NOTE_ELEMENT_CONFIG.textColor,
                        backgroundColor: NOTE_ELEMENT_CONFIG.backgroundColor,
                    },
                },
            });
        },
        hover: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) {
                onNoteDragPositionChange === null || onNoteDragPositionChange === void 0 ? void 0 : onNoteDragPositionChange(null);
                return;
            }
            const position = calculateNotePosition(clientOffset.x, clientOffset.y);
            if (position) {
                onNoteDragPositionChange === null || onNoteDragPositionChange === void 0 ? void 0 : onNoteDragPositionChange(Object.assign(Object.assign({}, position), { text: item.note.text || '' }));
            }
            else {
                onNoteDragPositionChange === null || onNoteDragPositionChange === void 0 ? void 0 : onNoteDragPositionChange(null);
            }
        },
    });
    /**
     * Обработчик нативных drag событий для файлов.
     * Предотвращает стандартное поведение только если перетаскиваются файлы.
     * Не мешает react-dnd событиям (когда файлов нет в dataTransfer).
     */
    const handleFileDragEvent = useCallback((e) => {
        var _a, _b;
        const hasFiles = ((_b = (_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.files.length) !== null && _b !== void 0 ? _b : 0) > 0;
        if (hasFiles) {
            e.preventDefault();
            e.stopPropagation();
        }
    }, []);
    /**
     * Разделяет файлы на изображения и текстовые
     */
    const categorizeFiles = useCallback((files) => {
        const images = [];
        const texts = [];
        const textExtensions = ['.txt', '.md', '.json', '.csv', '.log'];
        files.forEach((file) => {
            const type = file.type.toLowerCase();
            const name = file.name.toLowerCase();
            if (type.startsWith('image/')) {
                images.push(file);
            }
            else if (type.startsWith('text/') ||
                type === 'application/json' ||
                textExtensions.some((ext) => name.endsWith(ext))) {
                texts.push(file);
            }
            else {
                // По умолчанию считаем текстовым файлом
                texts.push(file);
            }
        });
        return { images, texts };
    }, []);
    /**
     * Обработчик drop - обрабатывает перетаскивание файлов на доску
     */
    const handleDrop = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!canEdit)
            return;
        const files = Array.from(((_a = e.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) || []);
        // Если нет файлов, это может быть react-dnd - не обрабатываем
        if (files.length === 0)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Сохраняем позицию курсора для вставки файлов
        const stage = store.get(stageRefAtom);
        if (stage) {
            const container = stage.getContent().parentElement;
            if (container) {
                const worldCoords = getWorldCoordinates(e.clientX, e.clientY, stage, container);
                if (worldCoords) {
                    pointerPositionRef.current = worldCoords;
                }
            }
        }
        // Разделяем и обрабатываем файлы
        const { images, texts } = categorizeFiles(files);
        // Обработка изображений
        if (images.length > 0) {
            if (images.length > 1) {
                yield (onPasteImages === null || onPasteImages === void 0 ? void 0 : onPasteImages(images));
            }
            else {
                yield (onPasteImage === null || onPasteImage === void 0 ? void 0 : onPasteImage(images[0]));
            }
        }
        // Обработка текстовых файлов
        for (const textFile of texts) {
            try {
                const text = yield textFile.text();
                if (text.trim()) {
                    onPasteText === null || onPasteText === void 0 ? void 0 : onPasteText(text);
                }
            }
            catch (error) {
                console.error('Ошибка при чтении текстового файла:', error);
            }
        }
    }), [
        canEdit,
        onPasteImage,
        onPasteImages,
        onPasteText,
        store,
        pointerPositionRef,
        categorizeFiles,
    ]);
    /**
     * Подключает обработчики drag-and-drop к контейнеру Stage.
     * React-dnd для заметок и нативные обработчики для файлов.
     */
    useEffect(() => {
        var _a;
        const stageContainer = (_a = store.get(stageRefAtom)) === null || _a === void 0 ? void 0 : _a.getContent();
        if (!stageContainer)
            return;
        const container = stageContainer.parentElement;
        if (!container)
            return;
        // Подключаем react-dnd drop для заметок
        drop(container);
        // Добавляем нативные обработчики для файлов
        container.addEventListener('dragover', handleFileDragEvent);
        container.addEventListener('dragenter', handleFileDragEvent);
        container.addEventListener('dragleave', handleFileDragEvent);
        container.addEventListener('drop', handleDrop);
        return () => {
            container.removeEventListener('dragover', handleFileDragEvent);
            container.removeEventListener('dragenter', handleFileDragEvent);
            container.removeEventListener('dragleave', handleFileDragEvent);
            container.removeEventListener('drop', handleDrop);
        };
    }, [drop, handleFileDragEvent, handleDrop, store]);
};
